import { useMemo, useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useMerchant, usePaymentSettings } from "@auth";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  FormGroup,
  FormLabel,
  FormError,
  TextInput,
  RadioCard,
  RadioGroup,
  Button,
  SwitchInput,
  SwitchTag,
  SwitchLabel,
} from "@components";
import {
  BatchSummaryModal,
  MessageTemplateFields,
  PreviewModal,
  InvoiceRequestTypeSelector,
  PaymentProfileFormField,
} from "./PaymentRequestDrawers/components";
import { isValidMobile, isValidEmail, invoicesRequestedTokenMap } from "@utils";
import {
  buildRequests,
  calcMaxRequestableAmount,
  calcMaxRequestableRows,
} from "./PaymentRequestDrawers/utils";
import {
  useBatchWorkflow,
  useTemplates,
} from "./PaymentRequestDrawers/hooks";
import { usePdfStatements } from "@flags";

const BatchRequestDrawer = ({ isOpen, onClose, customerData, onComplete }) => {
  const pdfStatements = usePdfStatements();
  const { merchant } = useMerchant();
  const { paymentSettings } = usePaymentSettings();
  const { templates, loading: templatesLoading } = useTemplates();
  const {
    phase,
    rows,
    totals,
    initialiseRowsWithSendStatus,
    runBatch,
    resetState,
  } = useBatchWorkflow();

  const { corporateCardSurchargePercent = null } = paymentSettings ?? {};
  const paymentRequestsAllowCardDefault =
    paymentSettings?.paymentRequests?.allowCard ?? false;

  /* ---------------------------- form setup ----------------------------- */
  const {
    control,
    getValues,
    watch,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      invoicesRequested: "Overdue",
      invoicesRequestedAreMandatory: false,
      includePdfStatement: false,
      subject: "",
      emailBody: "",
      smsMessage: "",
      template: "none",
      allowCardPayment: false, // Default to false, will be updated when merchant loads
      paymentProfileId: "",
    },
  });

  const invoicesRequested = watch("invoicesRequested");
  const templateId = watch("template");

  console.log("invoicesRequested", invoicesRequested);

  const corporateCardSurchargePercentValue =
    !!corporateCardSurchargePercent && corporateCardSurchargePercent > 0
      ? corporateCardSurchargePercent
      : null;

  // Update form values once merchant data is fetched
  useEffect(() => {
    if (merchant) {
      setValue("allowCardPayment", paymentRequestsAllowCardDefault, {
        shouldValidate: false,
      });
    }
  }, [merchant, paymentRequestsAllowCardDefault, setValue]);

  // removed; moved into useApplyRequestValues after preparedRows

  // Force invoicesRequestedAreMandatory false when requesting no mandatory invoices
  useEffect(() => {
    if (invoicesRequested === "None") {
      setValue("invoicesRequestedAreMandatory", false, {
        shouldValidate: false,
      });
    }
  }, [invoicesRequested, setValue]);

  // request rows
  const preparedRows = useMemo(
    () =>
      customerData && customerData.length
        ? buildRequests(customerData, invoicesRequested)
        : [],
    [customerData, invoicesRequested]
  );

  /* ------------------- channels ---------------------------- */
  const [smsSelected, setSmsSelected] = useState(false);
  const [emailSelected, setEmailSelected] = useState(false);

  useEffect(() => {
    if (!preparedRows.length) return;

    // A channel is considered available only if the contact is valid AND the row has no amount error.
    const smsOk = preparedRows.some(
      (r) => !r.amountError && isValidMobile(r.phone)
    );
    const emailOk = preparedRows.some(
      (r) => !r.amountError && isValidEmail(r.email)
    );

    setSmsSelected(smsOk);
    setEmailSelected(!smsOk && emailOk);
  }, [preparedRows, isValidMobile, isValidEmail]);

  /* ---------------- Auto scroll for batch name error ------------------ */
  const batchNameRef = useRef(null);

  useEffect(() => {
    if (errors.name && batchNameRef.current) {
      batchNameRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors.name]);

  /* ---------------- availability counts for RadioCards ---------------- */
  const smsAvailable = useMemo(
    () => preparedRows.filter((r) => !r.phoneError).length,
    [preparedRows]
  );
  const emailAvailable = useMemo(
    () => preparedRows.filter((r) => !r.emailError).length,
    [preparedRows]
  );

  /* ---------------- other useful values ------------------------------- */
  const maxAmount = useMemo(
    () =>
      invoicesRequested === "None"
        ? calcMaxRequestableRows(preparedRows, smsSelected, emailSelected)
        : calcMaxRequestableAmount(preparedRows, smsSelected, emailSelected),
    [preparedRows, smsSelected, emailSelected, invoicesRequested]
  );

  /* ---------------- initialise workflow ------------------------------- */
  useEffect(() => {
    if (!preparedRows.length) return;
    initialiseRowsWithSendStatus(preparedRows, {
      sms: smsSelected,
      email: emailSelected,
    });
  }, [preparedRows, smsSelected, emailSelected]);

  /* ------------------- template autofill ------------------------------ */
  useEffect(() => {
    if (!templates || templatesLoading) return;
    if (templateId === "none") {
      setValue("subject", "", { shouldValidate: true });
      setValue("smsMessage", "", { shouldValidate: true });
      setValue("emailBody", "", { shouldValidate: true });
      return;
    }
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) {
      setValue("subject", tpl.emailSubject || "", { shouldValidate: true });
      setValue("smsMessage", tpl.smsMessage || "", { shouldValidate: true });
      setValue("emailBody", tpl.emailBody || "", { shouldValidate: true });
      clearErrors(["subject", "smsMessage", "emailBody"]);
    }
  }, [templateId, templates, setValue, clearErrors, templatesLoading]);

  /* ---------------- preview modal ------------------- */
  const [showPreview, setShowPreview] = useState(false);

  /* ------------------- summary modal control -------------------------- */
  const [showSummary, setShowSummary] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const handleSummaryClose = () => {
    setShowSummary(false);
    if (phase === "complete") {
      closeDrawer();
    }
  };

  /* ----------------------------- send batch --------------------------- */
  const sendBatch = async () => {
    const activeTemplate = templates?.find((t) => t.id === templateId);
    console.log("activeTemplate", activeTemplate);
    // Filter rows by selection state before sending
    const rowsToSend = rows.filter((r) =>
      selectedCustomerIds.includes(r.customerId)
    );
    await runBatch({
      strategy: { sms: smsSelected, email: emailSelected },
      rowsOverride: rowsToSend,
      meta: {
        batchName: getValues("name"),
        invoicesRequested: getValues("invoicesRequested"),
        allowAdditionalInvoices: true,
        invoicesRequestedAreMandatory:
          invoicesRequested === "None"
            ? false
            : getValues("invoicesRequestedAreMandatory"),
        includePdfStatement: getValues("includePdfStatement"),
        subject: getValues("subject"),
        smsMessage: getValues("smsMessage"),
        emailBody: getValues("emailBody"),
        allowCardPayment: getValues("allowCardPayment"),
        paymentProfileId: getValues("paymentProfileId") || null,
        templateId: activeTemplate?.id,
        sendAsPlainText: activeTemplate?.sendAsPlainText,
        messageTemplateName: activeTemplate?.name ?? null,
      },
      paymentSettings,
    });
    onComplete?.();
  };

  /* ----------------------------- drawer close ------------------------- */
  const closeDrawer = () => {
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
      reset();
      if (merchant) {
        setValue("allowCardPayment", paymentRequestsAllowCardDefault, {
          shouldValidate: false,
        });
      }
      setShowPreview(false);
      setShowSummary(false);
      setSmsSelected(false);
      setEmailSelected(false);
      setSelectedCustomerIds([]);
    }
  }, [
    isOpen,
    resetState,
    reset,
    merchant,
    paymentRequestsAllowCardDefault,
    setValue,
  ]);

  const isErrorStatus = (status) =>
    ["amountTooSmall", "noContactDetails", "noPhone", "noEmail"].includes(
      status
    );

  const customersAvailableForSending = useMemo(
    () =>
      rows && rows.length
        ? rows.filter((r) => !isErrorStatus(r.status)).map((r) => r.customerId)
        : [],
    [rows]
  );

  const getInvoicesRequested = () =>
    invoicesRequestedTokenMap[getValues("invoicesRequested")];

  return (
    <Drawer
      title="Payment request · Batch"
      isOpen={isOpen}
      toggle={closeDrawer}
    >
      <DrawerBody>
        <form onSubmit={handleSubmit(() => setShowSummary(true))}>
          {/* Batch name */}
          <FormGroup className="p-5 border-b mb-0">
            <FormLabel>Batch name</FormLabel>
            <TextInput
              name="name"
              control={control}
              required
              errors={errors.name}
              innerRef={batchNameRef}
            />
          </FormGroup>

          {/* Amount section */}
          <FormGroup className="p-5 border-b mb-0">
            <FormLabel>Which invoices would you like to be paid?</FormLabel>
            {preparedRows.length && (
              <InvoiceRequestTypeSelector
                control={control}
                overdueAmount={preparedRows.reduce(
                  (x, y) => x + (y.overdue || 0),
                  0
                )}
                dueThisMonthAmount={preparedRows.reduce(
                  (x, y) => x + (y.dueThisMonth || 0),
                  0
                )}
                allInvoicesAmount={preparedRows.reduce(
                  (x, y) => x + (y.balance || 0),
                  0
                )}
              />
            )}
            <FormError
              name="invoicesRequested"
              className="static p-1"
              errors={errors}
            />
            {invoicesRequested !== "None" && (
              <SwitchInput
                className="mt-6"
                name="invoicesRequestedAreMandatory"
                control={control}
              >
                <SwitchLabel>Make these invoices mandatory</SwitchLabel>
                <SwitchTag>
                  If enabled, customers must pay these invoices at a minimum
                </SwitchTag>
              </SwitchInput>
            )}
          </FormGroup>

          {/* Payment profile / card toggle */}
          {isOpen && (
            <FormGroup className="p-5 border-b mb-0">
              <PaymentProfileFormField
                control={control}
                setValue={setValue}
                corporateCardSurchargePercentValue={corporateCardSurchargePercentValue}
              />
            </FormGroup>
          )}

          {pdfStatements && (
            <FormGroup className="p-5 border-b mb-0">
              <SwitchInput
                className="mt-0"
                name="includePdfStatement"
                control={control}
              >
                <SwitchLabel>Attach statement</SwitchLabel>
                <SwitchTag>
                  If enabled, customers will also receive a current PDF
                  statement via email only
                </SwitchTag>
              </SwitchInput>
            </FormGroup>
          )}

          {/* Channels */}
          <FormGroup className="p-5 border-b mb-0">
            <FormLabel>Channels</FormLabel>
            <RadioGroup className="mt-4 flex-col gap-0">
              <div className="flex gap-3">
                <RadioCard
                  title="SMS"
                  disabled={smsAvailable < 1}
                  checked={smsSelected}
                  onClick={() =>
                    smsAvailable > 0 && setSmsSelected(!smsSelected)
                  }
                  description={`Available for ${smsAvailable} of ${preparedRows.length} customers`}
                />
                <RadioCard
                  title="Email"
                  disabled={emailAvailable < 1}
                  checked={emailSelected}
                  onClick={() =>
                    emailAvailable > 0 && setEmailSelected(!emailSelected)
                  }
                  description={`Available for ${emailAvailable} of ${preparedRows.length} customers`}
                />
              </div>
            </RadioGroup>
          </FormGroup>

          {/* Template & messages */}
          <MessageTemplateFields
            control={control}
            errors={errors}
            templates={templates}
            smsSelected={smsSelected}
            emailSelected={emailSelected}
          />
        </form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          type="button"
          onClick={() => {
            setShowPreview(true);
          }}
          disabled={!smsSelected && !emailSelected}
        >
          Preview
        </Button>

        <Button
          className="ml-auto"
          color="primary"
          type="button"
          onClick={handleSubmit(() => {
            // Initialize selection to all available-for-sending customers
            setSelectedCustomerIds(customersAvailableForSending);
            setShowSummary(true);
          })}
          disabled={!smsSelected && !emailSelected}
        >
          Review
        </Button>
      </DrawerFooter>

      <BatchSummaryModal
        title={getValues("name")}
        isOpen={showSummary}
        onClose={handleSummaryClose}
        control={control}
        currentAmount={
          invoicesRequested === "None" ? totals.succeeded || 0 : totals.amount
        }
        maxAmount={maxAmount}
        phase={phase}
        rows={rows}
        totals={totals}
        invoicesRequested={invoicesRequested}
        smsSelected={smsSelected}
        emailSelected={emailSelected}
        onSend={sendBatch}
        isRowSelected={(r) => selectedCustomerIds.includes(r.customerId)}
        onToggleRow={(r) =>
          setSelectedCustomerIds((prev) =>
            prev.includes(r.customerId)
              ? prev.filter((id) => id !== r.customerId)
              : [...prev, r.customerId]
          )
        }
        isErrorStatus={isErrorStatus}
      />
      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        smsSelected={smsSelected}
        emailSelected={emailSelected}
        smsMessage={watch("smsMessage")}
        emailMessage={watch("emailBody")}
        invoicesRequested={getInvoicesRequested()}
        isBatch
      />
    </Drawer>
  );
};

export default BatchRequestDrawer;
